/**
 * Tile grid — the collision and interaction substrate. Pure data + queries;
 * rendering is the world layer's problem.
 *
 * Convention: `tiles[row][col]`, row 0 at the top. World-space pixel (x, y)
 * maps to tile (floor(x / TILE), floor(y / TILE)).
 */
import { TILE } from '@shared/constants.js';

export type Facing = 'up' | 'down' | 'left' | 'right';

/**
 * Tile kinds for the hand-authored P0 maps. Walkability is a property of the
 * kind, mirroring US 01.01's walkable-surface list. (Water is walkable only
 * by boat — boats are P2; until then water is simply blocked.)
 */
export type TileKind =
  'grass' | 'road' | 'sidewalk' | 'sand' | 'dock' | 'interior' | 'wall' | 'water' | 'door';

const WALKABLE: ReadonlySet<TileKind> = new Set<TileKind>([
  'grass',
  'road',
  'sidewalk',
  'sand',
  'dock',
  'interior',
  'door',
]);

export interface TileGrid {
  cols: number;
  rows: number;
  tiles: TileKind[][];
  /**
   * Tiles solid regardless of kind — occupied by NPCs, furniture, parked
   * hazards. Keys from `blockedKey`. The tile KIND stays walkable (an NPC on
   * grass does not repaint the grass); the occupancy does the blocking.
   */
  blocked?: ReadonlySet<string>;
}

export function blockedKey(col: number, row: number): string {
  return `${col},${row}`;
}

/** Movement modes — US 20.04: the boat swims, feet walk. Transitions
 *  happen only at docks, which both modes may occupy. */
export type MoveMode = 'foot' | 'boat';

const BOATABLE: ReadonlySet<TileKind> = new Set<TileKind>(['water', 'dock']);

export function isWalkableKind(kind: TileKind, mode: MoveMode = 'foot'): boolean {
  return mode === 'boat' ? BOATABLE.has(kind) : WALKABLE.has(kind);
}

export function tileAt(grid: TileGrid, col: number, row: number): TileKind | undefined {
  return grid.tiles[row]?.[col];
}

/** Out-of-bounds is solid — the map edge is always a wall. */
export function isWalkable(
  grid: TileGrid,
  col: number,
  row: number,
  mode: MoveMode = 'foot',
): boolean {
  if (grid.blocked?.has(blockedKey(col, row))) return false;
  const kind = tileAt(grid, col, row);
  return kind !== undefined && isWalkableKind(kind, mode);
}

export function pixelToTile(px: number): number {
  return Math.floor(px / TILE);
}

export const FACING_DELTA: Record<Facing, { dx: number; dy: number }> = {
  up: { dx: 0, dy: -1 },
  down: { dx: 0, dy: 1 },
  left: { dx: -1, dy: 0 },
  right: { dx: 1, dy: 0 },
};
